import * as React from "react";

import {
  RobotoMarkdownLink,
  type RobotoMarkdownLinkProps,
} from "@/shared/components/RobotoMarkdown/RobotoMarkdownLink";

import {
  isAIInlineTimestampHighlightedLink,
  isAIInlineTimestampLink,
  parseAIInlineTimestampNs,
} from "./aiInlineTimestampLinks";
import { InlineTimestamp } from "./InlineTimestamp";
import { parseTimeFormatSettingLink } from "./parseTimeFormatSettingLink";
import { TimeFormatSettingLink } from "./TimeFormatSettingLink";

/**
 * Link renderer for AI thread markdown. Handles the thread-only `ai-ts:` /
 * `ai-ts-hl:` timestamps and the AI-authored `roboto://ui/time-format`
 * preference switch; everything else defers to {@link RobotoMarkdownLink}.
 *
 * Nothing here adds the "Set as t=0" action: a link that names an instant
 * carries it through whichever renderer draws it (Timestamp, VizInternalLink,
 * VizExternalLink, EntityLink), each of which floats it in the same hover
 * toolbar as its own actions.
 */
export const AgentThreadMarkdownLink: React.FC<
  React.PropsWithChildren<RobotoMarkdownLinkProps>
> = ({ href, children, ...rest }) => {
  const settingFormat =
    href === undefined ? undefined : parseTimeFormatSettingLink(href);
  if (settingFormat !== undefined) {
    return (
      <TimeFormatSettingLink format={settingFormat}>
        {children}
      </TimeFormatSettingLink>
    );
  }

  if (href !== undefined && isAIInlineTimestampLink(href)) {
    const ns = parseAIInlineTimestampNs(href);
    if (ns !== undefined) {
      return (
        <InlineTimestamp
          ns={ns}
          highlighted={isAIInlineTimestampHighlightedLink(href)}
        />
      );
    }
    // Fail closed: an `ai-ts:` / `ai-ts-hl:` link with an unparseable
    // payload signals an upstream plugin defect. Render the original
    // label as plain text rather than emitting a broken `<a>`, so the
    // bug doesn't hide behind innocuous-looking output.
    return <>{children}</>;
  }

  return (
    <RobotoMarkdownLink href={href} {...rest}>
      {children}
    </RobotoMarkdownLink>
  );
};
